import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminProvisioningService } from "@/services/admin-provisioning.service";
import type { ProvisionResourceForUserRequest } from "@/types/admin-provisioning";

/**
 * Mutation: provision a VM for a target tenant/user. On success, invalidates the admin
 * resources and orders lists so they reflect the new order/resource.
 */
export function useProvisionVmForUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ProvisionResourceForUserRequest) =>
      adminProvisioningService.provisionForUser(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resources"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "resources"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "orders"] });
    },
  });
}

/**
 * Live pricing preview for the provision page. Pass a debounced request (or null when the
 * form is incomplete); the query is enabled only when a tenant + resource items are present.
 */
export function useProvisionPreview(request: ProvisionResourceForUserRequest | null) {
  return useQuery({
    queryKey: ["admin", "provision-preview", request],
    queryFn: () => adminProvisioningService.previewProvisionForUser(request!),
    enabled:
      !!request &&
      !!request.targetTenantId &&
      request.resourceItems.length > 0,
    staleTime: 30_000,
  });
}
